import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getDescriptionHtml } from "../../essentials/input/text-area/RichTextInput";
import { JobInfo, JobSubjects } from "../Job";
import "../Job.scss";
import { selectPosition } from "../utils/SelectPosition";
import LabelValue from "./LabelValue";

export interface JobContentProps {
	jobInfo: JobInfo;
	setClicked?: any;
}

const selectPositionHelper = (jobInfo: JobInfo, t: any): string => {
	// If author accountType is not available, try to determine position directly
	if (!jobInfo.author.accountType) {
		if (jobInfo.subject === "staff") {
			return t(`sportData.positions.staff.${jobInfo.position}`);
		} else if (jobInfo.subject === "team" || jobInfo.subject === "athlete") {
			return jobInfo.sport
				? t(`sportData.positions.${jobInfo.sport}.${jobInfo.position}`)
				: jobInfo.position;
		}
		return jobInfo.position || "";
	}

	return selectPosition(
		jobInfo.position,
		jobInfo.subject,
		jobInfo.sport,
		jobInfo.author.accountType,
		t
	);
};

const JobContent: React.FC<JobContentProps> = ({ jobInfo, setClicked }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	return (
		<div
			className="job__content"
			onClick={() => {
				navigate(`/jobs/view/${jobInfo.id}`);
			}}
			// onMouseUp={() =>
			//   setTimeout(() => {
			//     setClicked(false);
			//   }, 200)
			// }
			// onMouseDown={() => setClicked(true)}
		>
			<div className="divider" />
			{
				<p className="job__content-title ">
					{jobInfo.subject === "staff" || jobInfo.subject === "athlete"
						? selectPositionHelper(jobInfo, t)
						: t("job.searchingForTeam")}
				</p>
			}
			<JobContentBody jobInfo={jobInfo} />
			{(jobInfo.subject === "staff" || jobInfo.subject === "athlete") && (
				<div
					className="job-description"
					dangerouslySetInnerHTML={{
						__html: getDescriptionHtml(jobInfo.description || ""),
					}}
				/>
			)}
		</div>
	);
};

export default JobContent;

interface JobContentBodyProps {
	jobInfo: JobInfo;
}

export const JobContentBody: React.FC<JobContentBodyProps> = ({ jobInfo }) => {
	const selectSubject = () => {
		switch (jobInfo.subject) {
			case JobSubjects.athlete:
				return <SearchForAthlete />;
			case JobSubjects.staff:
				return <SearchForStaff />;
			case JobSubjects.team:
				return <SearchForTeam />;
		}
	};

	const { t } = useTranslation();

	const LeagueLabelValue = () => {
		if (!jobInfo.league) return null;
		const leagueValue =
			typeof jobInfo.league === "object"
				? `${jobInfo.league.name} (${jobInfo.league.category})`
				: "";
		if (!leagueValue) return null;
		return (
			<LabelValue
				label={t("common.words.league")}
				value={leagueValue}
			/>
		);
	};

	const SearchForTeam = () => {
		return (
			<div className="info">
				<div>
					<LabelValue
						label={t("common.words.position")}
						value={selectPositionHelper(jobInfo, t)}
					/>
					<LeagueLabelValue />
					<LabelValue
						label={t("common.words.city")}
						value={jobInfo.city}
					/>
					{jobInfo.availableToTransfer && (
						<LabelValue
							label={t("job.availableToTransfer")}
							value=""
						/>
					)}
					{jobInfo.cartellinoStatus && (
						<LabelValue
							label={t("job.cartellinoStatus")}
							value={jobInfo.cartellinoStatus}
						/>
					)}
					{jobInfo.team && (
						<LabelValue
							label={t("common.words.currentTeam")}
							value={jobInfo.team}
						/>
					)}
				</div>
			</div>
		);
	};

	const SearchForAthlete = () => {
		return (
			<div className="info">
				<div>
					<LabelValue
						label={t("common.words.position")}
						value={selectPositionHelper(jobInfo, t)}
					/>
					<LeagueLabelValue />
					<LabelValue
						label={t("common.words.city")}
						value={jobInfo.city}
					/>
					<LabelValue
						label={t("job.statusPreference")}
						value={jobInfo.cartellinoStatus}
					/>
				</div>
				<div className="divider" />
			</div>
		);
	};

	const SearchForStaff = () => {
		return (
			<div className="info">
				<div>
					<LabelValue
						label={t("common.words.position")}
						value={selectPositionHelper(jobInfo, t)}
					/>
					<LabelValue
						label={t("common.words.city")}
						value={jobInfo.city}
					/>
				</div>
				<div className="divider" />
			</div>
		);
	};

	return <>{selectSubject()}</>;
};
